/**
 * SQLite Database Layer for pyxl Desktop
 *
 * Provides CRUD operations that mirror the Firestore API.
 * Uses @tauri-apps/plugin-sql for SQLite access.
 *
 * This module is only active in the Tauri desktop environment.
 * Web builds will use Firestore directly (see firestore.ts).
 */

import Database from '@tauri-apps/plugin-sql';
import { MIGRATIONS, SCHEMA_VERSION } from './schema';
import type { UserProfile, RunRecord, LeaderboardEntry } from '../firestore';

// Singleton database instance
let db: Database | null = null;

/**
 * Check if we're running inside Tauri (desktop) vs web browser.
 */
export function isTauri(): boolean {
  return '__TAURI_INTERNALS__' in window;
}

/**
 * Initialize the SQLite database.
 * Creates tables if they don't exist and runs pending migrations.
 * Safe to call multiple times (idempotent).
 */
export async function initDatabase(): Promise<Database> {
  if (db) return db;

  // Connect to SQLite (file stored in Tauri app data directory)
  db = await Database.load('sqlite:pyxl.db');

  // Run migrations
  await runMigrations(db);

  return db;
}

/**
 * Get the database instance. Throws if not initialized.
 */
export function getDatabase(): Database {
  if (!db) {
    throw new Error('Database not initialized. Call initDatabase() first.');
  }
  return db;
}

/**
 * Run schema migrations.
 */
async function runMigrations(database: Database): Promise<void> {
  // Check current version
  let currentVersion = 0;
  try {
    const rows = await database.select<{ version: number }[]>(
      'SELECT version FROM schema_version ORDER BY version DESC LIMIT 1'
    );
    if (rows.length > 0 && rows[0]) {
      currentVersion = rows[0].version;
    }
  } catch {
    // Table doesn't exist yet — that's fine, we'll create it
  }

  if (currentVersion >= SCHEMA_VERSION) return;

  // Run all migrations (they use IF NOT EXISTS, so idempotent)
  for (const migration of MIGRATIONS) {
    await database.execute(migration);
  }
}

// ─── User CRUD ──────────────────────────────────────────────

/**
 * Create or update a user profile in SQLite.
 */
export async function saveUserProfile(profile: UserProfile): Promise<void> {
  const database = getDatabase();
  await database.execute(
    `INSERT INTO users (uid, display_name, photo_url, created_at, total_runs, perfect_count, current_streak, best_streak, total_score)
     VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9)
     ON CONFLICT(uid) DO UPDATE SET
       display_name = $2,
       photo_url = $3,
       total_runs = $5,
       perfect_count = $6,
       current_streak = $7,
       best_streak = $8,
       total_score = $9`,
    [
      profile.uid,
      profile.displayName,
      profile.photoURL,
      profile.createdAt instanceof Date
        ? profile.createdAt.toISOString()
        : String(profile.createdAt),
      profile.stats.totalRuns,
      profile.stats.perfectCount,
      profile.stats.currentStreak,
      profile.stats.bestStreak,
      profile.stats.totalScore,
    ]
  );
}

/**
 * Get a user profile from SQLite.
 */
export async function getUserProfileLocal(uid: string): Promise<UserProfile | null> {
  const database = getDatabase();
  const rows = await database.select<UserRow[]>(
    'SELECT * FROM users WHERE uid = $1',
    [uid]
  );

  if (rows.length === 0 || !rows[0]) return null;

  const row = rows[0];
  return {
    uid: row.uid,
    displayName: row.display_name,
    photoURL: row.photo_url,
    createdAt: new Date(row.created_at),
    stats: {
      totalRuns: row.total_runs,
      perfectCount: row.perfect_count,
      currentStreak: row.current_streak,
      bestStreak: row.best_streak,
      totalScore: row.total_score,
    },
  };
}

/**
 * Update user stats after a run.
 */
export async function updateUserStatsLocal(
  uid: string,
  runScore: number,
  perfectCount: number
): Promise<void> {
  const database = getDatabase();
  await database.execute(
    `UPDATE users SET
       total_runs = total_runs + 1,
       perfect_count = perfect_count + $2,
       current_streak = CASE WHEN $3 > 0 THEN current_streak + 1 ELSE 0 END,
       best_streak = MAX(best_streak, CASE WHEN $3 > 0 THEN current_streak + 1 ELSE 0 END),
       total_score = total_score + $4
     WHERE uid = $1`,
    [uid, perfectCount, perfectCount, runScore]
  );
}

// ─── Run CRUD ──────────────────────────────────────────────

/**
 * Save a run and its level results to SQLite.
 * Returns the generated run ID.
 */
export async function saveRunLocal(run: Omit<RunRecord, 'id'>): Promise<string> {
  const database = getDatabase();
  const runId = crypto.randomUUID();

  await database.execute(
    `INSERT INTO runs (id, uid, playlist_id, playlist_type, completed_at, total_score, hash, synced)
     VALUES ($1, $2, $3, $4, $5, $6, $7, $8)`,
    [
      runId,
      run.uid,
      run.playlistId,
      run.playlistType,
      run.completedAt instanceof Date
        ? run.completedAt.toISOString()
        : String(run.completedAt),
      run.totalScore,
      run.hash,
      0, // Not synced yet
    ]
  );

  // Save level results
  for (const result of run.levelResults) {
    await database.execute(
      `INSERT INTO level_results (run_id, level_id, score, grade, attempts, time_ms)
       VALUES ($1, $2, $3, $4, $5, $6)`,
      [runId, result.levelId, result.score, result.grade, result.attempts, result.timeMs]
    );
  }

  return runId;
}

/**
 * Get runs for a user, ordered by most recent.
 */
export async function getUserRunsLocal(uid: string, limitCount: number = 20): Promise<RunRecord[]> {
  const database = getDatabase();

  const runRows = await database.select<RunRow[]>(
    `SELECT * FROM runs WHERE uid = $1 ORDER BY completed_at DESC LIMIT $2`,
    [uid, limitCount]
  );

  const runs: RunRecord[] = [];
  for (const row of runRows) {
    const levelRows = await database.select<LevelResultRow[]>(
      `SELECT * FROM level_results WHERE run_id = $1 ORDER BY id ASC`,
      [row.id]
    );

    runs.push({
      id: row.id,
      uid: row.uid,
      playlistId: row.playlist_id,
      playlistType: row.playlist_type as 'warmup' | 'practice' | 'challenge',
      completedAt: new Date(row.completed_at),
      totalScore: row.total_score,
      hash: row.hash,
      levelResults: levelRows.map((lr) => ({
        levelId: lr.level_id,
        score: lr.score,
        grade: lr.grade as 'Perfect' | 'Good' | 'Off',
        attempts: lr.attempts,
        timeMs: lr.time_ms,
      })),
    });
  }

  return runs;
}

/**
 * Get unsynced runs (for offline sync queue).
 */
export async function getUnsyncedRuns(): Promise<RunRecord[]> {
  const database = getDatabase();
  const runRows = await database.select<RunRow[]>(
    `SELECT * FROM runs WHERE synced = 0 ORDER BY completed_at ASC`
  );

  const runs: RunRecord[] = [];
  for (const row of runRows) {
    const levelRows = await database.select<LevelResultRow[]>(
      `SELECT * FROM level_results WHERE run_id = $1 ORDER BY id ASC`,
      [row.id]
    );

    runs.push({
      id: row.id,
      uid: row.uid,
      playlistId: row.playlist_id,
      playlistType: row.playlist_type as 'warmup' | 'practice' | 'challenge',
      completedAt: new Date(row.completed_at),
      totalScore: row.total_score,
      hash: row.hash,
      levelResults: levelRows.map((lr) => ({
        levelId: lr.level_id,
        score: lr.score,
        grade: lr.grade as 'Perfect' | 'Good' | 'Off',
        attempts: lr.attempts,
        timeMs: lr.time_ms,
      })),
    });
  }

  return runs;
}

/**
 * Mark a run as synced.
 */
export async function markRunSynced(runId: string): Promise<void> {
  const database = getDatabase();
  await database.execute(
    `UPDATE runs SET synced = 1 WHERE id = $1`,
    [runId]
  );
}

// ─── Leaderboard CRUD ──────────────────────────────────────

/**
 * Cache leaderboard entries locally.
 */
export async function cacheLeaderboard(
  leaderboardId: string,
  entries: LeaderboardEntry[]
): Promise<void> {
  const database = getDatabase();

  // Clear existing entries for this leaderboard
  await database.execute(
    `DELETE FROM leaderboard_entries WHERE leaderboard_id = $1`,
    [leaderboardId]
  );

  // Insert new entries
  for (const entry of entries) {
    await database.execute(
      `INSERT INTO leaderboard_entries (leaderboard_id, uid, display_name, photo_url, score, playlist_id, completed_at)
       VALUES ($1, $2, $3, $4, $5, $6, $7)`,
      [
        leaderboardId,
        entry.uid,
        entry.displayName,
        entry.photoURL,
        entry.score,
        entry.playlistId,
        entry.completedAt instanceof Date
          ? entry.completedAt.toISOString()
          : String(entry.completedAt),
      ]
    );
  }
}

/**
 * Get cached leaderboard entries.
 */
export async function getCachedLeaderboard(
  leaderboardId: string,
  limitCount: number = 50
): Promise<LeaderboardEntry[]> {
  const database = getDatabase();
  const rows = await database.select<LeaderboardRow[]>(
    `SELECT * FROM leaderboard_entries
     WHERE leaderboard_id = $1
     ORDER BY score DESC
     LIMIT $2`,
    [leaderboardId, limitCount]
  );

  return rows.map((row, index) => ({
    uid: row.uid,
    displayName: row.display_name,
    photoURL: row.photo_url,
    score: row.score,
    playlistId: row.playlist_id,
    completedAt: new Date(row.completed_at),
    rank: index + 1,
  }));
}

// ─── Sync Queue ──────────────────────────────────────────

/**
 * Add an action to the sync queue (for offline→online sync).
 */
export async function enqueueSyncAction(
  action: 'save_run' | 'update_stats' | 'submit_leaderboard' | 'submit_consistency',
  payload: unknown
): Promise<void> {
  const database = getDatabase();
  await database.execute(
    `INSERT INTO sync_queue (action, payload) VALUES ($1, $2)`,
    [action, JSON.stringify(payload)]
  );
}

/**
 * Get all pending sync actions.
 */
export async function getPendingSyncActions(): Promise<SyncQueueRow[]> {
  const database = getDatabase();
  return database.select<SyncQueueRow[]>(
    `SELECT * FROM sync_queue ORDER BY created_at ASC`
  );
}

/**
 * Remove a completed sync action.
 */
export async function removeSyncAction(id: number): Promise<void> {
  const database = getDatabase();
  await database.execute(
    `DELETE FROM sync_queue WHERE id = $1`,
    [id]
  );
}

/**
 * Increment retry count and record error for a sync action.
 */
export async function recordSyncError(id: number, error: string): Promise<void> {
  const database = getDatabase();
  await database.execute(
    `UPDATE sync_queue SET retry_count = retry_count + 1, last_error = $2 WHERE id = $1`,
    [id, error]
  );
}

/**
 * Close the database connection.
 */
export async function closeDatabase(): Promise<void> {
  if (db) {
    await db.close();
    db = null;
  }
}

// ─── Row types (SQLite result shapes) ──────────────────────

interface UserRow {
  uid: string;
  display_name: string;
  photo_url: string | null;
  created_at: string;
  total_runs: number;
  perfect_count: number;
  current_streak: number;
  best_streak: number;
  total_score: number;
}

interface RunRow {
  id: string;
  uid: string;
  playlist_id: string;
  playlist_type: string;
  completed_at: string;
  total_score: number;
  hash: string;
  synced: number;
}

interface LevelResultRow {
  id: number;
  run_id: string;
  level_id: string;
  score: number;
  grade: string;
  attempts: number;
  time_ms: number;
}

interface LeaderboardRow {
  leaderboard_id: string;
  uid: string;
  display_name: string;
  photo_url: string | null;
  score: number;
  playlist_id: string;
  completed_at: string;
}

export interface SyncQueueRow {
  id: number;
  action: string;
  payload: string;
  created_at: string;
  retry_count: number;
  last_error: string | null;
}
